import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, Check, ArrowRight, Star, ChevronRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BRAND } from "@/lib/brand";
import { getService, SERVICES, buildWaLink, serviceMessage, trackWhatsApp, SERVICE_GROUPS } from "@/lib/services";
import { SERVICE_CONTENT } from "@/lib/serviceContent";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  const content = SERVICE_CONTENT[slug];

  // SEO per page
  useEffect(() => {
    if (!content) return;
    document.title = content.metaTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", content.metaDesc);
  }, [content]);

  if (!service || !content) return <Navigate to="/services" replace />;

  const group = SERVICE_GROUPS.find((g) => g.key === service.group);
  const wa = buildWaLink(serviceMessage(service.name));
  const related = SERVICES.filter((s) => s.group === service.group && s.slug !== slug).slice(0, 3);

  return (
    <main data-testid="service-page" className="pt-32 md:pt-40">
      {/* Breadcrumb */}
      <nav data-testid="breadcrumb" aria-label="Breadcrumb" className="container-x text-[13.5px] text-ink/60 flex items-center gap-1.5 flex-wrap">
        <Link to="/" className="hover:text-brand">Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/services" className="hover:text-brand">Services</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-navy font-semibold">{service.name}</span>
      </nav>

      {/* Hero */}
      <section className="container-x mt-6 grid grid-cols-12 gap-8 md:gap-12 items-center">
        <div className="col-span-12 lg:col-span-6">
          <div className="text-[12.5px] font-bold uppercase tracking-widest text-brand">{group.title}</div>
          <h1 className="mt-2 text-[34px] md:text-[48px] font-extrabold text-navy leading-[1.08]">{content.h1}</h1>
          <p className="mt-4 text-[17px] md:text-[18px] text-ink/85 max-w-xl">{service.desc} Delivered by trained professionals across Bangalore — available 24/7.</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <a data-testid="svc-wa" href={wa} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp(service.name)} className="btn-wa">
              <MessageCircle className="w-5 h-5" /> Book on WhatsApp
            </a>
            <a data-testid="svc-call" href={`tel:${BRAND.phoneTel}`} className="btn-navy">
              <Phone className="w-5 h-5" /> Call {BRAND.phone}
            </a>
          </div>

          {service.note && (
            <p data-testid="svc-note" className="mt-5 text-[13px] text-care font-semibold border-l-2 border-care pl-3">{service.note}</p>
          )}
        </div>
        <div className="col-span-12 lg:col-span-6">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card bg-soft-blue">
            <img src={content.image} alt={content.alt} className="w-full h-full object-cover" loading="eager" />
          </div>
        </div>
      </section>

      {/* What's included + Who it's for */}
      <section className="container-x mt-14 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-7 bg-white rounded-3xl border border-navy/10 p-7 md:p-9 shadow-soft">
          <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">What's included</h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-3">
            {content.included.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[15.5px] text-ink/90">
                <span className="shrink-0 w-5 h-5 rounded-full bg-care text-white flex items-center justify-center mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="col-span-12 lg:col-span-5 bg-mint rounded-3xl p-7 md:p-9">
          <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">Who this is for</h2>
          <ul className="mt-5 space-y-3">
            {content.whoFor.map((w) => (
              <li key={w} className="flex items-start gap-2.5 text-[15.5px] text-ink/90">
                <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-brand" />
                {w}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works */}
      <section className="container-x mt-14">
        <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">How it works</h2>
        <ol className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-5">
          {["Call or WhatsApp us on " + BRAND.phone, "Share the patient's condition and area", "Get a personalised care plan and quote", "A trained professional arrives at your home"].map((s, i) => (
            <li key={i} className="bg-white rounded-2xl border border-navy/10 p-5 shadow-soft">
              <div className="w-10 h-10 rounded-full bg-care text-white flex items-center justify-center font-extrabold">{i + 1}</div>
              <p className="mt-3 text-[15px] text-ink/90">{s}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* FAQs */}
      <section className="container-x mt-14">
        <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">Frequently asked</h2>
        <Accordion type="single" collapsible className="mt-5 max-w-3xl">
          {content.faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`svc-faq-${i}`} className="border-b border-navy/15">
              <AccordionTrigger className="text-left text-navy font-bold text-[16.5px] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-ink/80 text-[15.5px] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Reviews strip */}
      <section className="container-x mt-14">
        <div className="bg-white rounded-3xl border border-navy/10 p-6 md:p-8 shadow-soft flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex">{[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}</div>
            <div className="font-bold text-navy text-[17px]">{BRAND.rating}★ · {BRAND.ratingCount} Google reviews</div>
          </div>
          <a href="https://www.google.com/search?q=velixa+health+reviews" target="_blank" rel="noopener noreferrer" className="btn-outline-navy text-[14px]">Read reviews on Google</a>
        </div>
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="container-x mt-14">
          <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">Related services</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {related.map((s) => (
              <a key={s.slug} data-testid={`related-${s.slug}`} href={buildWaLink(serviceMessage(s.name))} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp(s.name)} aria-label={`Chat on WhatsApp about ${s.name}`} className="bg-white rounded-2xl border border-navy/10 p-5 hover-care">
                <h3 className="font-bold text-navy text-[17px]">{s.name}</h3>
                <p className="mt-1.5 text-[14px] text-ink/75">{s.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1EB757]"><MessageCircle className="w-4 h-4" style={{ color: "#25D366" }} /> Chat on WhatsApp →</div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="mt-16 bg-navy text-white py-14">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="text-[28px] md:text-[40px] font-extrabold leading-[1.1] text-white">Ready to book {service.name.toLowerCase()}?</h2>
          <p className="mt-3 text-white/85">Available 24/7 across Bangalore. Pricing is personalised after a quick assessment.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href={wa} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp(service.name + " CTA")} className="btn-wa"><MessageCircle className="w-5 h-5" /> Book on WhatsApp</a>
            <a href={`tel:${BRAND.phoneTel}`} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-navy font-semibold hover:bg-white/90"><Phone className="w-5 h-5" /> Call {BRAND.phone}</a>
          </div>
        </div>
      </section>
    </main>
  );
}
